import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { MessageService } from 'primeng/api';
import { Constant } from 'src/app/shared/service/utils/contant';
import { ParameterService } from 'src/app/shared/service/params/param';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-synthese-recettes-nettes',
  templateUrl: './synthese-recettes-nettes.component.html',
  styleUrl: './synthese-recettes-nettes.component.scss'
})
export class SyntheseRecettesNettesComponent implements OnInit {

  @Input() titre: string = 'Synthèse des recettes nettes';

  exercice: number = new Date().getFullYear();
  dateDebut = `${new Date().getFullYear()}-01-01`;
  dateFin = `${new Date().getFullYear()}-12-31`;

  synthese: any[] = [];
  isLoaded = false;
  showHistogramme = false;
  chartData: any;
  chartOptions: any;

  constructor(
    public activeModal: NgbActiveModal,
    private paramService: ParameterService,
    private messageService: MessageService,
    private ngxService: NgxUiLoaderService
  ) { }

  ngOnInit(): void {
    this.chartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' }
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) => value + '%'
          }
        }
      }
    };
  }

  get dateDebutDisplay(): string {
    return this.formatDateFR(this.dateDebut);
  }

  set dateDebutDisplay(value: string) {
    this.dateDebut = this.parseDateFR(value);
  }

  get dateFinDisplay(): string {
    return this.formatDateFR(this.dateFin);
  }

  set dateFinDisplay(value: string) {
    this.dateFin = this.parseDateFR(value);
  }

  formatDateFR(iso?: string): string {
    if (!iso) return '';
    const [y, m, d] = (iso || '').split('-');
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
  }

  parseDateFR(fr: string): string {
    if (!fr) return '';
    const [d, m, y] = fr.split('/');
    if (!d || !m || !y || y.length !== 4) return '';
    return `${y}-${m}-${d}`;
  }

  close(): void {
    this.activeModal.dismiss();
  }

  loadData(): void {
    if (!this.dateDebut || !this.dateFin) {
      this.messageService.add({
        severity: 'warn',
        summary: Constant.suiviTitle,
        detail: 'Veuillez renseigner la période (début et fin)'
      });
      return;
    }
    this.ngxService.start();
    this.paramService.getBordereauSynthese({
      date_debut: this.dateDebut,
      date_fin: this.dateFin
    }).subscribe(
      (response: any) => {
        this.ngxService.stop();
        this.synthese = response.data || [];
        this.isLoaded = true;
        if (this.showHistogramme) {
          this.refreshChart();
        }
      },
      (error: any) => {
        this.ngxService.stop();
        this.isLoaded = true;
        this.synthese = [];
        this.messageService.add({
          severity: 'error',
          summary: Constant.suiviTitle,
          detail: error?.error?.message || 'Une erreur est survenue lors du chargement de la synthèse'
        });
      }
    );
  }

  imprimer(): void {
    if (!this.dateDebut || !this.dateFin) {
      this.messageService.add({
        severity: 'warn',
        summary: Constant.suiviTitle,
        detail: 'Veuillez renseigner la période (début et fin)'
      });
      return;
    }
    this.ngxService.start();
    this.paramService.printBordereauSynthese({ date_debut: this.dateDebut, date_fin: this.dateFin }).subscribe(
      (response: Blob) => {
        this.ngxService.stop();
        FileSaver.saveAs(response, 'synthese-recettes-nettes.pdf');
      },
      (error: any) => {
        this.ngxService.stop();
        this.messageService.add({
          severity: 'error',
          summary: Constant.suiviTitle,
          detail: 'Impossible de générer le PDF'
        });
      }
    );
  }

  toggleHistogramme(): void {
    this.showHistogramme = !this.showHistogramme;
    if (this.showHistogramme) {
      this.refreshChart();
    }
  }

  private refreshChart(): void {
    if (!this.synthese || this.synthese.length === 0) {
      this.chartData = null;
      return;
    }
    this.chartData = {
      labels: this.synthese.map(item => item.code),
      datasets: [
        {
          label: 'Taux de réalisation (%)',
          backgroundColor: '#2f80ed',
          data: this.synthese.map(item => this.getTaux(item))
        }
      ]
    };
  }

  getTaux(item: any): number {
    const prevision = Number(item.prevision_annuelle || 0);
    const titre = Number(item.titre_recettes_nettes || 0);
    if (prevision === 0) {
      return 0;
    }
    const ratio = (titre / prevision) * 100;
    return Math.round(ratio * 100) / 100;
  }
}
